import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import apiClient from '@/lib/api';
import { Search, Trash2, ShieldCheck, Mail, User as UserIcon, Ban, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

const AdminUsersPage = () => {
  const [data, setData] = useState({ users: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);

  const fetchUsers = async (searchTerm = '') => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: '100' });
      if (searchTerm) params.append('search', searchTerm);
      const res = await apiClient.get(`/admin/users?${params}`);
      setData(res.data);
    } catch (error) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchUsers(); }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchUsers(search);
  };

  const updateUser = async (userId, updates) => {
    try {
      await apiClient.patch(`/admin/users/${userId}`, updates);
      toast.success('User updated');
      fetchUsers(search);
      if (selectedUser?.user_id === userId) {
        setSelectedUser({ ...selectedUser, ...updates });
      }
    } catch (error) {
      toast.error('Failed to update user');
    }
  };

  const deleteUser = async (userId, email) => {
    if (!confirm(`Delete user "${email}"? All their data will be removed. This cannot be undone.`)) return;
    try {
      await apiClient.delete(`/admin/users/${userId}`);
      toast.success('User deleted');
      setSelectedUser(null);
      fetchUsers(search);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-users-page">
      <form onSubmit={handleSearch} className="flex space-x-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground z-10" />
          <Input
            data-testid="user-search-input"
            placeholder="Search by email, name, or user ID..."
            className="pl-10"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <Button type="submit" data-testid="search-button">Search</Button>
      </form>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center">
              <div className="w-10 h-10 border-4 border-pista border-t-transparent rounded-full animate-spin mx-auto"></div>
            </div>
          ) : (
            <div className="divide-y divide-border">
              <div className="px-4 py-3 flex items-center text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                <div className="flex-1">User</div>
                <div className="w-28 text-right">Joined</div>
                <div className="w-32 text-right">Status</div>
                <div className="w-24 text-right">Actions</div>
              </div>
              {data.users.map((user) => (
                <div
                  key={user.user_id}
                  className="px-4 py-3 flex items-center hover:bg-secondary/40 transition-colors cursor-pointer"
                  onClick={() => setSelectedUser(user)}
                  data-testid={`user-row-${user.user_id}`}
                >
                  <div className="flex-1 flex items-center space-x-3 min-w-0">
                    <Avatar className="h-9 w-9 flex-shrink-0">
                      <AvatarImage src={user.picture} />
                      <AvatarFallback>{user.name?.charAt(0)}</AvatarFallback>
                    </Avatar>
                    <div className="min-w-0">
                      <p className="font-medium truncate">{user.name}</p>
                      <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                    </div>
                  </div>
                  <div className="w-28 text-right text-sm text-muted-foreground">
                    {format(new Date(user.created_at), 'MMM d, yyyy')}
                  </div>
                  <div className="w-32 text-right space-x-1">
                    {user.is_admin && <Badge className="bg-pista text-primary-foreground text-xs">Admin</Badge>}
                    {user.totp_enabled && <Badge variant="secondary" className="text-xs">2FA</Badge>}
                    {user.is_suspended && <Badge variant="destructive" className="text-xs">Suspended</Badge>}
                  </div>
                  <div className="w-24 text-right" onClick={(e) => e.stopPropagation()}>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => deleteUser(user.user_id, user.email)}
                      data-testid={`delete-user-${user.user_id}`}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                </div>
              ))}
              {data.users.length === 0 && (
                <div className="p-8 text-center text-muted-foreground">
                  No users found
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <p className="text-xs text-muted-foreground">
        Showing {data.users.length} of {data.total} users
      </p>

      {/* User Detail Dialog */}
      <Dialog open={!!selectedUser} onOpenChange={(open) => !open && setSelectedUser(null)}>
        <DialogContent className="max-w-2xl" data-testid="user-detail-dialog">
          {selectedUser && (
            <>
              <DialogHeader>
                <DialogTitle>User Details</DialogTitle>
                <DialogDescription>Manage {selectedUser.email}</DialogDescription>
              </DialogHeader>

              <div className="space-y-4 py-2">
                <div className="flex items-center space-x-4">
                  <Avatar className="h-16 w-16">
                    <AvatarImage src={selectedUser.picture} />
                    <AvatarFallback className="text-lg">{selectedUser.name?.charAt(0)}</AvatarFallback>
                  </Avatar>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold">{selectedUser.name}</h3>
                    <p className="text-sm text-muted-foreground">{selectedUser.email}</p>
                    <p className="text-xs font-mono text-muted-foreground mt-1">{selectedUser.user_id}</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="p-3 bg-secondary/40 rounded-lg">
                    <p className="text-xs text-muted-foreground">Joined</p>
                    <p className="font-medium">{format(new Date(selectedUser.created_at), 'MMM d, yyyy h:mm a')}</p>
                  </div>
                  <div className="p-3 bg-secondary/40 rounded-lg">
                    <p className="text-xs text-muted-foreground">Last Updated</p>
                    <p className="font-medium">{format(new Date(selectedUser.updated_at), 'MMM d, yyyy h:mm a')}</p>
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 bg-secondary/40 rounded-lg">
                    <div>
                      <Label className="font-medium">Admin Access</Label>
                      <p className="text-xs text-muted-foreground">Grant full platform access</p>
                    </div>
                    <Switch
                      checked={selectedUser.is_admin || false}
                      onCheckedChange={(checked) => updateUser(selectedUser.user_id, { is_admin: checked })}
                      data-testid="toggle-admin"
                    />
                  </div>

                  <div className="flex items-center justify-between p-3 bg-secondary/40 rounded-lg">
                    <div>
                      <Label className="font-medium">Email Verified</Label>
                      <p className="text-xs text-muted-foreground">Mark email as verified</p>
                    </div>
                    <Switch
                      checked={selectedUser.email_verified || false}
                      onCheckedChange={(checked) => updateUser(selectedUser.user_id, { email_verified: checked })}
                      data-testid="toggle-email-verified"
                    />
                  </div>

                  <div className="flex items-center justify-between p-3 bg-secondary/40 rounded-lg">
                    <div>
                      <Label className="font-medium">Suspended</Label>
                      <p className="text-xs text-muted-foreground">Block user from signing in</p>
                    </div>
                    <Switch
                      checked={selectedUser.is_suspended || false}
                      onCheckedChange={(checked) => updateUser(selectedUser.user_id, { is_suspended: checked })}
                      data-testid="toggle-suspended"
                    />
                  </div>
                </div>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setSelectedUser(null)}>Close</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminUsersPage;
